/**
 * CEO Command Center v11 - Sync Service
 *
 * Coordinates P2P synchronization between peers:
 * - Handles sync protocol messages
 * - Encrypts/decrypts changesets with space keys
 * - Applies CRDT changes from peers
 * - Manages sync state
 *
 * @module services/sync/syncService
 * @version 11.0.0
 */

import { databaseService } from './database';
import { cryptoService } from './crypto';
import { transportService } from './transport';
import type {
  SyncMessage,
  SyncRequestMessage,
  SyncResponseMessage,
  ChangeMessage,
  AckMessage,
  EncryptedChangeset,
  CRSQLChange,
  Space,
  DeviceIdentity,
} from '@/types/sync';

// ============================================
// TYPES
// ============================================

type SyncEventHandler = (event: SyncEvent) => void;

interface SyncEvent {
  type: 'sync-started' | 'sync-completed' | 'sync-error' | 'changes-received' | 'changes-sent';
  spaceId?: string;
  peerId?: string;
  changeCount?: number;
  error?: string;
}

interface SpaceKeyCache {
  [spaceId: string]: CryptoKey;
}

// ============================================
// CONSTANTS
// ============================================

const SYNC_DEBOUNCE_MS = 500;

// ============================================
// SYNC SERVICE
// ============================================

class SyncServiceImpl {
  private identity: DeviceIdentity | null = null;
  private spaces: Space[] = [];
  private spaceKeys: SpaceKeyCache = {};
  private eventHandlers: Set<SyncEventHandler> = new Set();
  private pendingSyncRequests: Map<string, { spaceId: string; resolve: () => void; reject: (err: Error) => void }> = new Map();
  private syncDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _isSyncing = false;

  /**
   * Initialize the sync service with identity and spaces
   */
  async init(identity: DeviceIdentity, spaces: Space[]): Promise<void> {
    console.log('[SyncService] Initializing...');
    this.identity = identity;
    this.spaces = spaces;

    // Initialize transport
    await transportService.init(identity);

    // Register message handlers
    transportService.onMessage(this.handleMessage.bind(this));
    transportService.onPeerConnected(this.handlePeerConnected.bind(this));
    transportService.onPeerDisconnected(this.handlePeerDisconnected.bind(this));

    // Join rooms for all spaces
    for (const space of spaces) {
      await transportService.joinRoom(space.id);
      // Pre-cache space keys
      await this.getSpaceKey(space);
    }

    console.log('[SyncService] Initialized with', spaces.length, 'spaces');
  }

  /**
   * Shutdown sync service
   */
  shutdown(): void {
    console.log('[SyncService] Shutting down...');
    transportService.disconnectAll();
    this.spaceKeys = {};
    this.pendingSyncRequests.clear();
    this._isSyncing = false;
  }

  /**
   * Connect to a peer by ID
   */
  async connectToPeer(peerId: string): Promise<void> {
    await transportService.connect(peerId);
  }

  /**
   * Request sync from a connected peer for a specific space
   */
  async requestSync(peerId: string, spaceId: string): Promise<void> {
    const dbVersion = await databaseService.getDbVersion();

    const message: SyncRequestMessage = {
      type: 'sync-request',
      spaceId,
      sinceVersion: dbVersion.toString(),
    };

    this.emit({ type: 'sync-started', spaceId, peerId });
    this._isSyncing = true;

    await transportService.send(peerId, message);
  }

  /**
   * Broadcast local changes to all peers in a space
   */
  async broadcastChanges(spaceId: string, since: bigint = BigInt(0)): Promise<void> {
    const changes = await databaseService.getChanges(since);
    if (changes.length === 0) {
      return;
    }

    const space = this.spaces.find(s => s.id === spaceId);
    if (!space) {
      throw new Error(`Space not found: ${spaceId}`);
    }

    const encryptedChangeset = await this.encryptChangeset(spaceId, changes);
    const message: ChangeMessage = {
      type: 'change',
      changeset: encryptedChangeset,
    };

    await transportService.broadcast(spaceId, message);
    this.emit({ type: 'changes-sent', spaceId, changeCount: changes.length });
  }

  /**
   * Sync all spaces with connected peers
   */
  async syncAll(): Promise<void> {
    const peers = transportService.getConnectedPeers();
    if (peers.length === 0) {
      console.log('[SyncService] No peers connected');
      return;
    }

    for (const peerId of peers) {
      for (const space of this.spaces) {
        await this.requestSync(peerId, space.id);
      }
    }
  }

  /**
   * Register an event handler
   */
  onEvent(handler: SyncEventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => this.eventHandlers.delete(handler);
  }

  /**
   * Check if currently syncing
   */
  isSyncing(): boolean {
    return this._isSyncing;
  }

  /**
   * Get connected peer count
   */
  getPeerCount(): number {
    return transportService.getConnectedPeers().length;
  }

  // ============================================
  // PRIVATE METHODS
  // ============================================

  private handleMessage(peerId: string, message: SyncMessage): void {
    console.log('[SyncService] Received message:', message.type, 'from', peerId);

    switch (message.type) {
      case 'sync-request':
        this.handleSyncRequest(peerId, message);
        break;
      case 'sync-response':
        this.handleSyncResponse(peerId, message);
        break;
      case 'change':
        this.handleChange(peerId, message);
        break;
      case 'ack':
        this.handleAck(peerId, message);
        break;
      case 'error':
        console.error('[SyncService] Error from peer:', message.message);
        this.emit({ type: 'sync-error', peerId, error: message.message });
        break;
    }
  }

  private async handleSyncRequest(peerId: string, message: SyncRequestMessage): Promise<void> {
    const { spaceId, sinceVersion } = message;

    // Check if we have this space
    const space = this.spaces.find(s => s.id === spaceId);
    if (!space) {
      await transportService.send(peerId, {
        type: 'error',
        code: 'SPACE_NOT_FOUND',
        message: `Space not found: ${spaceId}`,
      });
      return;
    }

    try {
      const since = BigInt(sinceVersion);
      const changes = await databaseService.getChanges(since);
      const currentVersion = await databaseService.getDbVersion();

      let encryptedChangeset: EncryptedChangeset | null = null;
      if (changes.length > 0) {
        encryptedChangeset = await this.encryptChangeset(spaceId, changes);
      }

      const response: SyncResponseMessage = {
        type: 'sync-response',
        spaceId,
        changes: encryptedChangeset,
        currentVersion: currentVersion.toString(),
      };

      await transportService.send(peerId, response);
      console.log('[SyncService] Sent sync response with', changes.length, 'changes');
    } catch (err) {
      console.error('[SyncService] Error handling sync request:', err);
      await transportService.send(peerId, {
        type: 'error',
        code: 'SYNC_ERROR',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }

  private async handleSyncResponse(peerId: string, message: SyncResponseMessage): Promise<void> {
    const { spaceId, changes } = message;
    this._isSyncing = false;

    if (!changes) {
      console.log('[SyncService] No changes in sync response');
      this.emit({ type: 'sync-completed', spaceId, peerId, changeCount: 0 });
      return;
    }

    try {
      const decryptedChanges = await this.decryptChangeset(changes);
      await databaseService.applyChanges(decryptedChanges);

      // Send acknowledgment
      const ack: AckMessage = {
        type: 'ack',
        spaceId,
        version: message.currentVersion,
      };
      await transportService.send(peerId, ack);

      this.emit({ type: 'sync-completed', spaceId, peerId, changeCount: decryptedChanges.length });
      console.log('[SyncService] Applied', decryptedChanges.length, 'changes from sync response');
    } catch (err) {
      console.error('[SyncService] Error applying sync changes:', err);
      this.emit({ type: 'sync-error', spaceId, peerId, error: err instanceof Error ? err.message : 'Unknown error' });
    }
  }

  private async handleChange(peerId: string, message: ChangeMessage): Promise<void> {
    const { changeset } = message;

    try {
      const decryptedChanges = await this.decryptChangeset(changeset);
      await databaseService.applyChanges(decryptedChanges);

      // Send acknowledgment
      const currentVersion = await databaseService.getDbVersion();
      const ack: AckMessage = {
        type: 'ack',
        spaceId: changeset.spaceId,
        version: currentVersion.toString(),
      };
      await transportService.send(peerId, ack);

      this.emit({ type: 'changes-received', spaceId: changeset.spaceId, peerId, changeCount: decryptedChanges.length });
      console.log('[SyncService] Applied', decryptedChanges.length, 'real-time changes');
    } catch (err) {
      console.error('[SyncService] Error applying real-time changes:', err);
    }
  }

  private handleAck(_peerId: string, message: AckMessage): void {
    console.log('[SyncService] Received ack for space:', message.spaceId, 'version:', message.version);
  }

  private handlePeerConnected(peerId: string): void {
    console.log('[SyncService] Peer connected:', peerId);
    // Debounce sync to avoid flooding
    if (this.syncDebounceTimer) {
      clearTimeout(this.syncDebounceTimer);
    }
    this.syncDebounceTimer = setTimeout(() => {
      this.syncAll().catch(console.error);
    }, SYNC_DEBOUNCE_MS);
  }

  private handlePeerDisconnected(peerId: string): void {
    console.log('[SyncService] Peer disconnected:', peerId);
  }

  private async getSpaceKey(space: Space): Promise<CryptoKey> {
    if (this.spaceKeys[space.id]) {
      return this.spaceKeys[space.id];
    }

    // Decrypt space key using device key
    const deviceKey = databaseService.getDeviceKey();
    if (!deviceKey) {
      throw new Error('Device not unlocked');
    }

    const encryptedKeyData = cryptoService.base64ToBytes(space.encryptedKey);
    // First 12 bytes are nonce, rest is ciphertext
    const nonce = encryptedKeyData.slice(0, 12);
    const ciphertext = encryptedKeyData.slice(12);

    const spaceKeyBytes = await cryptoService.decrypt(ciphertext, nonce, deviceKey);
    const spaceKey = await cryptoService.importKey(spaceKeyBytes);

    this.spaceKeys[space.id] = spaceKey;
    return spaceKey;
  }

  private async encryptChangeset(spaceId: string, changes: CRSQLChange[]): Promise<EncryptedChangeset> {
    const space = this.spaces.find(s => s.id === spaceId);
    if (!space) {
      throw new Error(`Space not found: ${spaceId}`);
    }

    const spaceKey = await this.getSpaceKey(space);

    // Serialize changes
    const changesJson = JSON.stringify(changes, (_, value) =>
      typeof value === 'bigint' ? value.toString() : value
    );
    const changesBytes = new TextEncoder().encode(changesJson);

    // Encrypt
    const { ciphertext, nonce } = await cryptoService.encrypt(changesBytes, spaceKey);

    // Sign
    // Note: In production, we'd sign with the device's private key
    // For now, just use the sender key as identifier
    const signature = await cryptoService.hash(ciphertext);

    return {
      spaceId,
      nonce: cryptoService.bytesToBase64(nonce),
      ciphertext: cryptoService.bytesToBase64(ciphertext),
      signature: cryptoService.bytesToBase64(signature),
      senderKey: this.identity?.publicKey || '',
      timestamp: Date.now(),
    };
  }

  private async decryptChangeset(changeset: EncryptedChangeset): Promise<CRSQLChange[]> {
    const space = this.spaces.find(s => s.id === changeset.spaceId);
    if (!space) {
      throw new Error(`Space not found: ${changeset.spaceId}`);
    }

    const spaceKey = await this.getSpaceKey(space);

    const nonce = cryptoService.base64ToBytes(changeset.nonce);
    const ciphertext = cryptoService.base64ToBytes(changeset.ciphertext);

    const decrypted = await cryptoService.decrypt(ciphertext, nonce, spaceKey);
    const changesJson = new TextDecoder().decode(decrypted);

    // Parse and restore bigints
    const changes = JSON.parse(changesJson) as CRSQLChange[];
    return changes.map(change => ({
      ...change,
      col_version: BigInt(change.col_version),
      db_version: BigInt(change.db_version),
    }));
  }

  private emit(event: SyncEvent): void {
    for (const handler of this.eventHandlers) {
      handler(event);
    }
  }
}

// Export singleton instance
export const syncService = new SyncServiceImpl();
export { SyncServiceImpl };
